import type { BadgeColor } from '@customerio/pluma-components/react';
import type { Newsletter } from '@/services/db/newsletter/entity';
import { isEmptyString } from '@/utils/string';
import type { PickerMessage } from './message-picker-data';

/** A newsletter entry in the row's picker. */
export type NewsletterOption = { value: string; label: string };

/** A variation entry; the badge index and language suffix ride along for the option renderers. */
export type NewsletterVariationOption = {
  value: string;
  label: string;
  variation?: number;
  language?: string;
};

/** The variation select's "no template narrowing" entry. */
export const ALL_VARIATIONS_VALUE = '';

// New picks commit numeric ids, matching what the legacy editor writes
// (`Number(newsletter.id)` / numeric `template_id`); ids that arrived on the
// wire keep their type because re-selecting never rewrites them here.
export function toWireId(value: string): number | string {
  const id = Number(value);
  return Number.isNaN(id) ? value : id;
}

type NewsletterName = Pick<Newsletter, 'id' | 'name'>;

/**
 * Whether the by-id name lookup must run: a stored selection the loaded
 * pages can't label, and no remembered pick to label it either.
 */
export function needsOffPageLookup(
  selectedValue: string | undefined,
  loadedNewsletters: readonly NewsletterName[],
  pickedNewsletter: NewsletterOption | null,
): boolean {
  if (selectedValue == null) {
    return false;
  }
  if (pickedNewsletter != null && pickedNewsletter.value === selectedValue) {
    return false;
  }
  return !loadedNewsletters.some((newsletter) => newsletter.id === selectedValue);
}

function storedSelectionLabel(
  selectedValue: string,
  pickedNewsletter: NewsletterOption | null,
  offPageNewsletter: NewsletterName | undefined,
): string {
  if (pickedNewsletter != null && pickedNewsletter.value === selectedValue) {
    return pickedNewsletter.label;
  }
  if (offPageNewsletter != null && offPageNewsletter.id === selectedValue) {
    return offPageNewsletter.name;
  }
  return selectedValue;
}

/**
 * The picker's option list: the loaded pages, with a stable entry prepended
 * for a stored selection those pages don't hold (picked out of search
 * results, off the loaded pages, or deleted). Its label comes from the
 * remembered pick or the by-id lookup, with the raw id as the last resort —
 * like the legacy editor.
 */
export function newsletterPickerOptions(
  loadedNewsletters: readonly NewsletterName[],
  selectedValue: string | undefined,
  pickedNewsletter: NewsletterOption | null,
  offPageNewsletter: NewsletterName | undefined,
): NewsletterOption[] {
  const options = loadedNewsletters.map((newsletter) => ({ value: newsletter.id, label: newsletter.name }));
  if (selectedValue == null || options.some((option) => option.value === selectedValue)) {
    return options;
  }
  const stored = {
    value: selectedValue,
    label: storedSelectionLabel(selectedValue, pickedNewsletter, offPageNewsletter),
  };
  return [stored, ...options];
}

/** The A–H variation badge palette, mirroring the legacy editor's color labels. */
const VARIATION_COLOR_LABELS: { label: string; color: BadgeColor }[] = [
  { label: 'A', color: 'purple' },
  { label: 'B', color: 'clementine' },
  { label: 'C', color: 'blue' },
  { label: 'D', color: 'raspberry' },
  { label: 'E', color: 'yellow' },
  { label: 'F', color: 'green' },
  { label: 'G', color: 'red' },
  { label: 'H', color: 'grey' },
];

/** The 1-based variation's badge label and color, or null past the palette (or for the All Variations entry). */
export function variationColorLabel(variation: number | undefined): { label: string; color: BadgeColor } | null {
  if (variation == null) {
    return null;
  }
  return VARIATION_COLOR_LABELS[variation - 1] ?? null;
}

function variationLanguage(language: string | null | undefined, hasLanguageVariations: boolean): string | undefined {
  if (!hasLanguageVariations) {
    return undefined;
  }
  if (isEmptyString(language)) {
    return 'Default';
  }
  return language ?? undefined;
}

/**
 * The selected newsletter's segmentable variations, in catalog order: label
 * from the variation's subject, value its template id. The language suffix
 * renders only when any variation carries one; a no-language variation then
 * reads "Default", like the legacy editor.
 */
export function newsletterVariationOptions(
  segmentable: readonly PickerMessage[],
  newsletterId: number | string | null,
): NewsletterVariationOption[] {
  if (newsletterId == null) {
    return [];
  }
  const variations = segmentable.filter(
    (message) =>
      message.newsletter_id != null &&
      String(message.newsletter_id) === String(newsletterId) &&
      message.is_segmentable_variation === true,
  );
  const hasLanguageVariations = variations.some((variation) => !isEmptyString(variation.language));
  const options: NewsletterVariationOption[] = [];
  for (const variation of variations) {
    // Only per-variation catalog entries qualify as segmentable variations,
    // and those always carry their template id; the guard keeps the option
    // type honest without a cast.
    if (variation.template_id == null) {
      continue;
    }
    options.push({
      label: variation.subject,
      value: variation.template_id,
      variation: variation.variation ?? undefined,
      language: variationLanguage(variation.language, hasLanguageVariations),
    });
  }
  return options;
}
